import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Filter, Eye, Check, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import FormInput from '../FormInput';
import Pagination from '../Pagination';
import api from '../api';

const DepensesTab = () => {
  const [depenses, setDepenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedDepense, setSelectedDepense] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const [formData, setFormData] = useState({
    montant: '',
    motif: '',
    categorie: '',
    justificatif: null,
    commentaire: ''
  });

  const statusOptions = [
    { value: 'EN_ATTENTE', label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'APPROUVE', label: 'Approuvé', color: 'bg-blue-100 text-blue-800' },
    { value: 'REJETE', label: 'Rejeté', color: 'bg-red-100 text-red-800' },
    { value: 'PAYE', label: 'Payé', color: 'bg-green-100 text-green-800' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [depensesRes, categoriesRes] = await Promise.all([
        api.get('/finance/depenses/'),
        api.get('/finance/categories-depense/')
      ]);
      setDepenses(depensesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('montant', formData.montant);
      formDataToSend.append('motif', formData.motif);
      formDataToSend.append('categorie', formData.categorie);
      formDataToSend.append('commentaire', formData.commentaire || '');
      formDataToSend.append('demandeur', 1); // TODO: Récupérer l'utilisateur connecté
      
      if (formData.justificatif) {
        formDataToSend.append('justificatif', formData.justificatif);
      }

      if (modalMode === 'add') {
        await api.post('/finance/depenses/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Dépense ajoutée avec succès');
      } else {
        await api.put(`/finance/depenses/${selectedDepense.id}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Dépense modifiée avec succès');
      }

      fetchData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde de la dépense');
    }
  };

  const handleEdit = (depense) => {
    setSelectedDepense(depense);
    setFormData({
      montant: depense.montant,
      motif: depense.motif,
      categorie: depense.categorie_id,
      justificatif: null,
      commentaire: depense.commentaire || ''
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ?')) {
      try {
        await api.delete(`/finance/depenses/${id}/`);
        fetchData();
        toast.success('Dépense supprimée avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression de la dépense');
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      const depense = depenses.find(d => d.id === id);
      await api.put(`/finance/depenses/${id}/`, {
        ...depense,
        statut: 'APPROUVE'
      });
      fetchData();
      toast.success('Dépense approuvée avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'approbation de la dépense');
    }
  };

  const handleReject = async (depense) => {
    try {
      await api.put(`/finance/depenses/${depense.id}/`, {
        ...depense,
        statut: 'REJETE',
        approbateur: 1 // TODO: Récupérer l'utilisateur connecté
      });
      fetchData();
      toast.success('Dépense rejetée avec succès');
    } catch (error) {
      console.error('Erreur lors du rejet:', error);
      toast.error('Erreur lors du rejet de la dépense');
    }
  };

  const resetForm = () => {
    setFormData({
      montant: '',
      motif: '',
      categorie: '',
      justificatif: null,
      commentaire: ''
    });
    setSelectedDepense(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalMode('add');
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusColor = (statut) => {
    const status = statusOptions.find(s => s.value === statut);
    return status ? status.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (statut) => {
    const status = statusOptions.find(s => s.value === statut);
    return status ? status.label : statut;
  };

  // Filtrage des données
  const filteredDepenses = depenses.filter(depense => {
    const matchSearch = depense.motif?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       depense.categorie_libelle?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !filterStatus || depense.statut === filterStatus;
    const matchCategory = !filterCategory || depense.categorie_id.toString() === filterCategory;
    return matchSearch && matchStatus && matchCategory;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDepenses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDepenses.length / itemsPerPage);

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.libelle
  }));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton d'ajout et recherche */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une dépense..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            >
              <option value="">Tous les statuts</option>
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
          >
            <option value="">Toutes les catégories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.libelle}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle Dépense
        </button>
      </div>

      {/* Tableau des dépenses */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motif
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Aucune dépense trouvée
                  </td>
                </tr>
              ) : (
                currentItems.map((depense) => (
                  <tr key={depense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(depense.date_demande)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {depense.categorie_libelle}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {depense.motif}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(depense.montant)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(depense.statut)}`}>
                        {getStatusLabel(depense.statut)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(depense)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifier"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {depense.statut === 'EN_ATTENTE' && (
                          <button
                            onClick={() => handleApprove(depense.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Approuver"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(depense.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Modal d'ajout/édition */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'add' ? 'Nouvelle Dépense' : 'Modifier la Dépense'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Catégorie"
            type="react-select"
            options={categoryOptions}
            value={formData.categorie}
            onChange={(value) => handleInputChange('categorie', value)}
            required
          />

          <FormInput
            label="Montant (FCFA)"
            type="number"
            value={formData.montant}
            onChange={(e) => handleInputChange('montant', e.target.value)}
            min="0"
            required
          />

          <FormInput
            label="Motif"
            type="textarea"
            value={formData.motif}
            onChange={(e) => handleInputChange('motif', e.target.value)}
            rows={3}
            required
          />

          <FormInput
            label="Justificatif"
            type="file"
            onChange={(e) => handleInputChange('justificatif', e.target.files[0])}
            helpText="Formats acceptés: PDF, JPG, PNG (max 5MB)"
          />

          <FormInput
            label="Commentaire"
            type="textarea"
            value={formData.commentaire}
            onChange={(e) => handleInputChange('commentaire', e.target.value)}
            rows={2}
            placeholder="Commentaire optionnel"
          />

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
            >
              {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DepensesTab; 